import { getUiLocale } from '../../utils/uiTranslation';
import { localizeApplicantNotification } from '../../utils/localizedSystemMessages';
import { useEffect } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../../firebase';
import { getNotificationsOptimized } from './DatabaseOptimizer';

const translateNotification = (notification, t) => t ? localizeApplicantNotification(notification) : notification;

// Debounce function to reduce processing frequency
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Calculate which checklist day corresponds to today's date
const calculateChecklistDay = (startDate, today) => {
  // Only show notifications on business days (Monday-Friday)
  const todayDayOfWeek = today.getDay();
  if (todayDayOfWeek === 0 || todayDayOfWeek === 6) {
    return null; // No notifications on weekends
  }

  // Normalize dates to avoid time zone issues
  const normalizedToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const normalizedStartDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());

  // If today is before start date, no notification
  if (normalizedToday < normalizedStartDate) {
    return null;
  }

  // Calculate business days elapsed from start date to today
  let currentDate = new Date(normalizedStartDate);
  let businessDaysElapsed = 0;

  while (currentDate <= normalizedToday) {
    // If this is a business day, increment counter
    if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
      // Check if we've reached today
      if (currentDate.toDateString() === normalizedToday.toDateString()) {
        // Today is a business day - check if it's within the 5-day period
        if (businessDaysElapsed < 5) {
          return `day${businessDaysElapsed + 1}`;
        } else {
          return null; // Beyond the 5-day checklist period
        }
      }
      businessDaysElapsed++;
    }

    // Move to next day
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return null;
};

// Calculate the actual date for a given checklist day
const calculateActualDate = (startDate, dayKey) => {
  const dayIndex = parseInt(dayKey.replace('day', '')) - 1;
  let currentDate = new Date(startDate);
  let businessDaysAdded = 0;

  while (businessDaysAdded < dayIndex) {
    currentDate.setDate(currentDate.getDate() + 1);
    // Skip weekends
    if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
      businessDaysAdded++;
    }
  }

  return currentDate;
};

// Calculate time since last stage change
export const calculateTimeSinceMove = (lastStageChange, t = null) => {
  if (!lastStageChange) return { days: 0, text: t ? t('notifications.duration.overWeek') : 'over a week' };

  const now = new Date();
  const moveDate = new Date(lastStageChange);
  const diffInDays = Math.floor((now - moveDate) / (1000 * 60 * 60 * 24));

  if (diffInDays >= 30) {
    const months = Math.floor(diffInDays / 30);
    return {
      days: diffInDays,
      text: months === 1 ? (t ? t('notifications.duration.oneMonth') : 'one month') : (t ? t('notifications.duration.months', { count: months }) : `${months} months`)
    };
  } else if (diffInDays >= 14) {
    const weeks = Math.floor(diffInDays / 7);
    return {
      days: diffInDays,
      text: weeks === 1 ? (t ? t('notifications.duration.oneWeek') : 'one week') : (t ? t('notifications.duration.weeks', { count: weeks }) : `${weeks} weeks`)
    };
  } else if (diffInDays >= 7) {
    return { days: diffInDays, text: t ? t('notifications.duration.oneWeek') : 'one week' };
  } else {
    return { days: diffInDays, text: t ? t('notifications.duration.overWeek') : 'over a week' };
  }
};

// Check if we should process unmoved candidate notification
export const shouldCheckUnmovedCandidate = (data, now) => {
  // Only check if candidate has been in position for at least 7 days
  const lastStageChange = data.lastStageChange;
  if (!lastStageChange) return false;

  const moveTime = new Date(lastStageChange).getTime();
  const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

  return (now - moveTime) > ONE_WEEK_MS;
};

// Helper function to get person data
const getPersonData = async (applicantId) => {
  try {
    const personRef = ref(database, `people/${applicantId}`);
    const personSnapshot = await get(personRef);
    return personSnapshot.val();
  } catch (error) {
    console.error('Error fetching person data:', error);
    return null;
  }
};

// Process all notification types in one efficient pass - OPTIMIZED VERSION
export const processNotificationsEfficiently = async (connections, authenticatedUID, t = null) => {
  try {
    // 🚀 OPTIMIZED: Use the enhanced optimized method that checks all notification types
    const optimizedNotifications = await getNotificationsOptimized(authenticatedUID);
    return t ? optimizedNotifications.map((n) => translateNotification(n, t)) : optimizedNotifications;
  } catch (error) {
    console.error('Error with optimized notifications, falling back to legacy:', error);
  }

  // FALLBACK: Legacy method if optimized version fails
  const notifications = [];
  const now = Date.now();

  for (const [connectionId, data] of Object.entries(connections)) {
    if (!connectionId.startsWith(`${authenticatedUID}_`)) continue;

    // CRITICAL FIX: Properly extract applicantId for both imported and authenticated candidates
    let applicantId, applicantType;
    if (connectionId.includes('_imported_')) {
      // Format: {employerUID}_imported_{applicantId}
      applicantId = connectionId.split('_imported_')[1];
      applicantType = 'imported';
    } else {
      // Format: {employerUID}_{applicantId}
      applicantId = connectionId.split('_')[1];
      applicantType = 'authenticated';
    }

    // Fetch person data once for this connection
    let candidateName = 'Unknown Candidate';
    try {
      if (applicantType === 'imported') {
        // Get name from imported_profiles node (NEW path first)
        let profileRef = ref(database, `imported_profiles/${authenticatedUID}/${applicantId}`);
        let profileSnapshot = await get(profileRef);
        if (!profileSnapshot.exists()) {
          // Fallback to legacy path
          profileRef = ref(database, `imported_profiles/${applicantId}`);
          profileSnapshot = await get(profileRef);
        }
        const profileData = profileSnapshot.val();
        candidateName = profileData?.name || profileData?.username || 'Unknown Candidate';
      } else {
        // Get name from people node (authenticated users)
        const personData = await getPersonData(applicantId);
        candidateName = personData?.username || 'Unknown Candidate';
      }
    } catch (error) {
      console.error('Error fetching candidate name:', error);
    }

    const connectionNotifications = [];

    // Unread chat check
    if (data.newMsgEmpl === "true") {
      connectionNotifications.push({
        id: `chat_${connectionId}`,
        type: 'unread_chat',
        title: t ? t('notifications.types.unread_chat.title') : 'New Message',
        description: t ? t('notifications.types.unread_chat.description', { candidate: candidateName }) : `You have a new message from ${candidateName}. Make sure to respond promptly.`,
        candidateName: candidateName,
        jobTitle: data.jobAppliedFor || 'Unknown Position',
        jobId: data.jobId || '',
        applicantId: applicantId,
        timestamp: data.latestMessage || now, // Use latestMessage instead of lastMessageTime
        unread: true
      });
    }

    // Assessment completion check
    if (data.assessments) {
      for (const [assessmentId, assessmentData] of Object.entries(data.assessments)) {
        // Check for assessments that are completed OR evaluated but haven't sent notification yet
        const isCompleted = assessmentData.response?.status === 'completed';
        const isEvaluated = ['evaluated', 'manual_review'].includes(assessmentData.response?.status);
        const notificationNotSent = !assessmentData.notificationSent;

        if ((isCompleted || isEvaluated) && notificationNotSent) {
          // CRITICAL FIX: Fetch assessment title from companies node, not connections!
          let assessmentTitle = 'an assessment';
          try {
            const assessmentRef = ref(database, `companies/${authenticatedUID}/assessments/${assessmentId}`);
            const assessmentSnapshot = await get(assessmentRef);
            if (assessmentSnapshot.exists()) {
              const assessmentDetails = assessmentSnapshot.val();
              assessmentTitle = assessmentDetails.title || assessmentDetails.name || 'an assessment';
            }
          } catch (error) {
            console.warn('Could not fetch assessment title for notification:', error);
          }

          connectionNotifications.push({
            id: `assessment_${connectionId}_${assessmentId}`,
            type: 'assessment_completed',
            title: t ? t('notifications.types.assessment_completed.title') : 'Assessment Completed',
            description: t ? t('notifications.types.assessment_completed.description', { candidate: candidateName || 'A candidate', assessment: assessmentTitle }) : `${candidateName || 'A candidate'} has completed ${assessmentTitle}. Click to review their responses.`,
            assessmentTitle,
            candidateName: candidateName || 'Unknown Candidate',
            jobTitle: data.jobAppliedFor || 'Unknown Position',
            jobId: data.jobId || '',
            applicantId: applicantId,
            timestamp: assessmentData.response.submittedAt || now,
            unread: true
          });
        }
      }
    }

    // Unmoved candidate check
    if (shouldCheckUnmovedCandidate(data, now)) {
      const timeSinceMove = calculateTimeSinceMove(data.lastStageChange, t);
      if (timeSinceMove.days >= 14) {
        // Check if notification was recently dismissed
        const dismissedTime = data.unmovedNotificationDismissed;
        let shouldShow = true;

        if (dismissedTime) {
          const dismissedTimestamp = new Date(dismissedTime).getTime();
          const timeSinceDismissed = now - dismissedTimestamp;
          const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

          // Don't show notification if it was dismissed within the last week
          if (timeSinceDismissed < ONE_WEEK_MS) {
            shouldShow = false;
          }
        }

        if (shouldShow) {
          connectionNotifications.push({
            id: `unmoved_${connectionId}`,
            type: 'unmoved_candidate',
            durationDays: timeSinceMove.days,
            title: t ? t('notifications.types.unmoved_candidate.title') : 'Candidate Needs Attention',
            description: t ? t('notifications.types.unmoved_candidate.description', { candidate: candidateName, duration: timeSinceMove.text }) : `${candidateName} has been in the same stage for ${timeSinceMove.text}. Please review their application and make a decision on whether to move them to the next stage.`,
            candidateName: candidateName,
            jobTitle: data.jobAppliedFor || 'Unknown Position',
            jobId: data.jobId || '',
            applicantId: applicantId,
            timestamp: now,
            unread: true
          });
        }
      }
    }

    // First Week Checklist notifications check
    // Logistics checklist incomplete
    if (data.logisticsChecklist) {
      const checklist = data.logisticsChecklist.checklist || [];
      const progress = data.logisticsProgress || {};
      const total = checklist.length;
      const completed = Object.values(progress).filter(Boolean).length;
      if (total > 0 && completed < total) {
        const remaining = total - completed;
        connectionNotifications.push({
          id: `logistics_${connectionId}`,
          type: 'logistics_incomplete',
          title: t ? t('notifications.types.logistics_incomplete.title') : 'Logistics Checklist Pending',
          description: t ? t('notifications.types.logistics_incomplete.description', { candidate: candidateName, remaining }) : `${candidateName} has ${remaining} logistics task${remaining !== 1 ? 's' : ''} remaining.`,
          candidateName: candidateName,
          jobTitle: data.jobAppliedFor || 'Unknown Position',
          jobId: data.jobId || '',
          applicantId: applicantId,
          timestamp: now,
          unread: true
        });
      }
    }
    if (data.firstWeekChecklist && data.checklistProgress) {
      const checklistData = data.firstWeekChecklist;
      const progressData = data.checklistProgress;

      // Only show notifications if there's a start date
      if (!checklistData.startDate) {
        // Skip this notification check but continue with others
        return;
      }

      // Calculate which checklist day corresponds to today's actual date
      const today = new Date();
      const startDate = new Date(checklistData.startDate);
      const todayDayKey = calculateChecklistDay(startDate, today);

      if (todayDayKey && checklistData.checklist && checklistData.checklist[todayDayKey]) {
        const todayTasks = checklistData.checklist[todayDayKey];
        const todayProgress = progressData[todayDayKey] || {};

        const completedTasks = Object.values(todayProgress).filter(Boolean).length;
        const totalTasks = todayTasks.length;
        const remainingTasks = totalTasks - completedTasks;

        if (remainingTasks > 0) {
          // Calculate the actual date for this checklist day
          const checklistDate = calculateActualDate(startDate, todayDayKey);
          const locale = getUiLocale();
          const dayName = checklistDate.toLocaleDateString(locale, { weekday: 'long' });
          const dateStr = checklistDate.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
          const dayIndex = parseInt(todayDayKey.replace('day', ''));
          const dayDisplay = t ? t('details.checklist.dayLabel', { dayNumber: dayIndex, weekday: dayName, date: dateStr }) : `Day ${dayIndex} (${dayName}, ${dateStr})`;

          connectionNotifications.push({
            id: `checklist_${connectionId}_${todayDayKey}`,
            type: 'checklist_reminder',
            title: t ? t('notifications.types.checklist_reminder.title') : 'First Week Tasks Pending',
            description: t ? t('notifications.types.checklist_reminder.description', { candidate: candidateName, remaining: remainingTasks, day: dayDisplay }) : `${candidateName} has ${remainingTasks} task${remainingTasks > 1 ? 's' : ''} remaining for ${dayDisplay}. Click to view their checklist progress.`,
            candidateName: candidateName,
            jobTitle: data.jobAppliedFor || 'Unknown Position',
            jobId: data.jobId || '',
            applicantId: applicantId,
            timestamp: now,
            unread: true,
            remainingTasks: remainingTasks,
            totalTasks: totalTasks,
            day: dayDisplay,
            dayDate: checklistDate.toISOString(),
            dayIndex: parseInt(todayDayKey.replace('day', ''), 10)
          });
        }
      }
    }

    notifications.push(...connectionNotifications);
  }

  return t ? notifications.map((n) => translateNotification(n, t)) : notifications;
};

// User activity detection hook
export const useUserActivity = (callback, delay = 1000) => {
  useEffect(() => {
    const handleUserActivity = debounce(() => {
      callback();
    }, delay);

    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
    window.addEventListener('click', handleUserActivity);
    window.addEventListener('scroll', handleUserActivity);

    return () => {
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);
    };
  }, [callback, delay]);
}; 
